import React from 'react'
import { Alert, Button, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { DraftCampaign } from '@marketing-milk/interfaces'
import { format, fromUnixTime } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { DateResolutionStrategy, resolveDraftDates } from './campaignDates.helpers'

interface Props {
  staleDrafts: DraftCampaign[]
  resolutionByDraftId: Record<number, DateResolutionStrategy>
  setResolution: (draftId: number, strategy: DateResolutionStrategy) => void
  applyToAll: (strategy: DateResolutionStrategy) => void
  now: Date
  timezone: string
}

// Deliberately rendered with explicit dates and times rather than relative ones: resolving a stale
// start date is a decision about exact times, and "3 days ago" does not support it.
const formatRange = (startTime: number, endTime: number, timezone: string) =>
  `${format(utcToZonedTime(fromUnixTime(startTime), timezone), 'MMM d, yyyy h:mm a')} – ${format(
    utcToZonedTime(fromUnixTime(endTime), timezone),
    'MMM d, yyyy h:mm a'
  )}`

export const StaleDraftDatesNotice = ({
  staleDrafts,
  resolutionByDraftId,
  setResolution,
  applyToAll,
  now,
  timezone,
}: Props) => {
  if (staleDrafts.length === 0) return null

  return (
    <Alert severity="warning" variant="outlined" className="w-full">
      <div className="flex flex-col space-y-4">
        <div>
          <p className="font-semibold">
            {staleDrafts.length === 1
              ? 'This campaign was scheduled to start on a day that has already passed.'
              : `${staleDrafts.length} campaigns were scheduled to start on a day that has already passed.`}
          </p>
          <p>
            Choose how to move each one forward. The budget stays the same either way, so a shorter
            campaign spends it faster.
          </p>
        </div>

        <div className="space-x-2">
          <Button variant="outlined" size="small" onClick={() => applyToAll('keepEnd')}>
            Apply to all: start today, keep end date
          </Button>
          <Button variant="outlined" size="small" onClick={() => applyToAll('shiftWindow')}>
            Apply to all: start today, keep the same length
          </Button>
        </div>

        {staleDrafts.map(draft => {
          const current = resolveDraftDates(draft.config.campaign, undefined, now, timezone)
          const keepEnd = resolveDraftDates(draft.config.campaign, 'keepEnd', now, timezone)
          const shiftWindow = resolveDraftDates(draft.config.campaign, 'shiftWindow', now, timezone)

          return (
            <div key={draft.id} className="border-t pt-2">
              <div className="font-semibold">{draft.config.campaign.name}</div>
              <div className="text-sm">
                Currently: {formatRange(current.startTime, current.endTime, timezone)} (
                {current.dayCount} days)
              </div>

              <RadioGroup
                value={resolutionByDraftId[draft.id] ?? ''}
                onChange={event =>
                  setResolution(draft.id, event.target.value as DateResolutionStrategy)
                }
              >
                <FormControlLabel
                  value="keepEnd"
                  control={<Radio color="primary" />}
                  disabled={!current.keepEndAvailable}
                  label={
                    <span className="text-sm">
                      <span className="font-semibold">Keep the end date</span> — finishes as
                      planned, but the campaign is shorter and spends its budget faster.
                      {current.keepEndAvailable ? (
                        <>
                          {' '}
                          {formatRange(keepEnd.startTime, keepEnd.endTime, timezone)} (
                          {keepEnd.dayCount} days)
                        </>
                      ) : (
                        <> Unavailable — the end date is not after today&apos;s start time.</>
                      )}
                    </span>
                  }
                />
                <FormControlLabel
                  value="shiftWindow"
                  control={<Radio color="primary" />}
                  label={
                    <span className="text-sm">
                      <span className="font-semibold">Keep the same length</span> — spends at the
                      planned pace, but finishes later.{' '}
                      {formatRange(shiftWindow.startTime, shiftWindow.endTime, timezone)} (
                      {shiftWindow.dayCount} days)
                    </span>
                  }
                />
              </RadioGroup>

              {/* The radio above is disabled but stays selected, so without this the AE sees a
                  blocked publish button and no reason for it. */}
              {resolutionByDraftId[draft.id] === 'keepEnd' && !current.keepEndAvailable && (
                <p className="text-sm font-semibold">
                  The choice made earlier is no longer possible — the end date is not after
                  today&apos;s start time. Choose &quot;keep the same length&quot; to publish this
                  campaign.
                </p>
              )}
            </div>
          )
        })}
      </div>
    </Alert>
  )
}
